import captureSentryException from '@/features/app/functions/captureSentryException';
import { Cart } from '@/features/cart/types/cart';
import { CartItem } from '@/features/cart/types/cart-item';
import useFeatureFlags from '@/features/feature-flags/hooks/useFeatureFlags';
import transformOrderItem from '@/features/orders/functions/transformOrderItem';
import { RawOrder } from '@/features/orders/types/raw-order-response';
import { ReservedStockOrder } from '@/features/orders/types/reserved-stock-order';
import processTireDotField from '@/features/products/functions/processTireDotField';
import transformTireSize from '@/features/products/functions/transformTireSize';
import { Tire } from '@/features/products/types/arktype/Tire';
import { TireDot } from '@/features/products/types/arktype/TireDot';
import { TireSize } from '@/features/products/types/arktype/TireSize';
import { ProductBundle } from '@/features/products/types/product-bundle';
import { ProductBundleItem } from '@/features/products/types/product-bundle-item';
import { SpecialPrice } from '@/features/products/types/special-price';
import { Promotion } from '@/features/promotions/types/promotion';
import standardProductBundleItemPopulation from '@/shared/functions/population-helpers/standardProductBundleItemPopulation';
import standardPromotionPopulation from '@/shared/functions/population-helpers/standardPromotionPopulation';
import standardTireDotPopulation from '@/shared/functions/population-helpers/standardTireDotPopulation';
import standardTireSizePopulation from '@/shared/functions/population-helpers/standardTireSizePopulation';
import useAxios from '@/shared/hooks/useAxios';
import useEnhancedQueryOptions from '@/shared/hooks/useEnhancedUseQueryOptions';
import { StandardApiResponse } from '@/shared/types/standard-api-reponse';
import { UseQueryOptions } from '@tanstack/react-query';
import { isNil } from 'lodash-es';
import { useTranslation } from 'react-i18next';

export default function useCartQueryOptions(options: Omit<UseQueryOptions<Cart>, 'queryKey'> = {}) {
  const axios = useAxios();
  const featureFlags = useFeatureFlags();
  const { t } = useTranslation();

  return useEnhancedQueryOptions({
    queryKey: ['carts'],
    queryFn: async () => {
      return axios
        .get<
          StandardApiResponse<
            Array<{
              id: number;
              promotion?: Promotion[] | null | undefined;
              promotionAmount?: number | null | undefined;
              subTotal: number;
              taxAmount: number;
              totalAmount: number;
              cartItem: Array<{
                id: number;
                quantity: number;
                promotion?: Promotion | null | undefined;
                promotionAmount?: number | null | undefined;
                productBundle?: Omit<ProductBundle, 'productBundleItem'> & {
                  productBundleItem: Array<
                    Omit<ProductBundleItem, 'tireSize'> & {
                      tireSize: Omit<TireSize, 'tire'> & {
                        tire: Tire[];
                      };
                      tireDot: TireDot;
                    }
                  >;
                };
                reservedStockItem?: {
                  id: number;
                  order: ReservedStockOrder;
                } | null;
                itemPromotions?: Promotion[] | null | undefined;
                tireSize?:
                  | (Omit<TireSize, 'tire'> & {
                      tire: Tire[];
                    })
                  | null;
                tireDot?: TireDot | null;
                specialPrice?: SpecialPrice | null;
                additionalTire?:
                  | null
                  | []
                  | undefined
                  | {
                      id: number;
                      quantity: number;
                      tireSize: Omit<TireSize, 'tire'> & {
                        tire: Tire[];
                      };
                      tireDot: TireDot;
                    }
                  | null;
              }>;
            }>
          >
        >(`/api/v1/carts`, {
          params: {
            'populate[cartItem]': 1,
            'populate[cartItem][additionalTire]': 1,
            ...standardPromotionPopulation('promotion'),
            ...standardTireSizePopulation('cartItem.tireSize', {
              populateRoot: true,
              populateTireDot: featureFlags.page.product.enableTireDot,
            }),
            ...(featureFlags.page.product.enableTireDot
              ? standardTireDotPopulation('cartItem.tireDot', { populateRoot: true })
              : {}),
            'populate[cartItem][productBundle]': 1,
            ...standardProductBundleItemPopulation('cartItem.productBundle.productBundleItem', {
              populateTireDot: featureFlags.page.product.enableTireDot,
            }),
            'populate[cartItem][reservedStockItem]': 1,
            ...standardPromotionPopulation('cartItem.promotion'),
            limit: 0,
          },
        })
        .then((res) => {
          return (
            res.data.data?.[0] ?? {
              id: 0,
              promotion: [],
              promotionAmount: 0,
              subTotal: 0,
              totalAmount: 0,
              taxAmount: 0,
              cartItem: [],
            }
          );
        })
        .then((cart) => {
          return {
            id: cart.id,
            promotion: cart.promotion || [],
            promotionAmount: cart.promotionAmount || 0,
            subTotal: cart.subTotal || 0,
            totalAmount: cart.totalAmount || 0,
            taxAmount: cart.taxAmount || 0,
            cartItem: (cart.cartItem || [])
              .map((item) => {
                if (
                  typeof item.productBundle === 'object' &&
                  !Array.isArray(item.productBundle) &&
                  !isNil(item.productBundle)
                ) {
                  const { productBundleItem, ...rest } = item.productBundle;

                  return {
                    type: 'productBundle',
                    id: item.id,
                    quantity: item.quantity,
                    promotion: item.promotion,
                    promotionAmount: item.promotionAmount,
                    productBundle: {
                      ...rest,
                      productBundleItem: productBundleItem.map((item) => {
                        return {
                          ...item,
                          tireSize: transformTireSize(
                            item.tireSize,
                            featureFlags.page.product.enableTireDot
                          ),
                          tireDot: processTireDotField(
                            item.tireSize,
                            item.tireDot,
                            'tireDot',
                            featureFlags.page.product.enableTireDot
                          ),
                        } satisfies ProductBundleItem;
                      }),
                    } satisfies ProductBundle,
                  } satisfies CartItem;
                } else if (item.reservedStockItem && !Array.isArray(item.reservedStockItem)) {
                  const reservedStockOrder = {
                    ...item.reservedStockItem.order,
                    orderItem: (item.reservedStockItem.order as RawOrder).orderItem.map((item) => {
                      return transformOrderItem(item, {
                        enableTireDot: featureFlags.page.product.enableTireDot,
                      });
                    }),
                  };

                  const currentOrderItem = reservedStockOrder.orderItem.find((orderItem) =>
                    orderItem.type === 'tireSize' &&
                    item.tireSize?.id === orderItem.tireSize?.id &&
                    featureFlags.page.product.enableTireDot
                      ? item.tireDot?.id === orderItem.tireDot?.id
                      : true
                  );

                  if (!currentOrderItem) {
                    return null;
                  }

                  const tireSize = transformTireSize(
                    item.tireSize!,
                    featureFlags.page.product.enableTireDot
                  );
                  const tireDot: TireDot = processTireDotField(
                    item.tireSize!,
                    item.tireDot!,
                    'tireDot',
                    featureFlags.page.product.enableTireDot
                  );

                  return {
                    type: 'reservedStock',
                    id: item.id,
                    quantity: item.quantity,
                    promotion: item.promotion,
                    promotionAmount: item.promotionAmount,
                    tireSize: tireSize,
                    tireDot: tireDot,
                    reservedStock: {
                      ...currentOrderItem,
                      orderId: item.reservedStockItem.order.id,
                      orderNumber: item.reservedStockItem.order.orderNumber,
                    },
                  } satisfies CartItem;
                } else {
                  return {
                    type: 'tireSize',
                    id: item.id,
                    quantity: item.quantity,
                    promotion: item.promotion,
                    promotionAmount: item.promotionAmount,
                    itemPromotions: item.itemPromotions ?? [],
                    tireSize: {
                      ...transformTireSize(item.tireSize!, featureFlags.page.product.enableTireDot),
                      specialPrice: item.tireSize!.specialPrice!,
                    },
                    tireDot: processTireDotField(
                      item.tireSize!,
                      item.tireDot!,
                      'tireDot',
                      featureFlags.page.product.enableTireDot
                    ),
                    specialPrice: item.specialPrice,
                    additionalTire:
                      item.additionalTire && !Array.isArray(item.additionalTire)
                        ? {
                            id: item.additionalTire.id,
                            quantity: item.additionalTire.quantity,
                            tireSize: transformTireSize(
                              item.additionalTire.tireSize!,
                              featureFlags.page.product.enableTireDot
                            ),
                            tireDot: processTireDotField(
                              item.additionalTire.tireSize!,
                              item.additionalTire.tireDot!,
                              'tireDot',
                              featureFlags.page.product.enableTireDot
                            ),
                          }
                        : undefined,
                  } satisfies CartItem;
                }
              })
              .filter((item) => item !== null) satisfies CartItem[],
          };
        })
        .catch((err) => {
          captureSentryException(err);
          return Promise.reject(t('page.cart.feedback.cartFetchErrorTitle'));
        });
    },
    staleTime: 0,
    ...options,
  });
}
